import { useEffect, useMemo, useRef, useState } from "react";
import { useWatch, type UseFormSetValue } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import {
  FormSection,
  ControlledComboBox,
  ControlledTextArea,
} from "@excent-one/react/form";
import { FormFieldSet } from "@progress/kendo-react-all";
import { withMemoizedContainer } from "@excent-one/react/utils/children";
import { useAuthContext } from "@excent-one/auth";
import { getAllIncidentFileFieldTypes } from "@phrmpc/infrastructure/phrmpc/api/incident-file-field-types";
import { getAllDecisions } from "@phrmpc/infrastructure/phrmpc/api/decisions";
import { getAllDisciplinaryActions } from "@phrmpc/infrastructure/phrmpc/api/disciplinary-actions";
import { useCaseDocumentChain } from "../hooks/useCaseDocumentChain";
import { buildFileFieldTypeMap } from "../utils/fileFieldType";
import { toEnumOptions } from "../utils/toEnumOptions";
import CaseDocumentTabStrip, { getPendingSubmit } from "./CaseDocumentTabStrip";
import ResignationTerminationRow from "./ResignationTerminationRow";
import { useIncidentMonitoringGating } from "./IncidentMonitoringGatingContext";
import type { IncidentFormSectionProps, IncidentFormValues } from "../types";

export interface MonitoringCaseSectionProps extends IncidentFormSectionProps {
  setValue: UseFormSetValue<IncidentFormValues>;
  isAwol: boolean;
}

/**
 * Renders the case-document chain (Task 157786) as a Kendo TabStrip plus the Decision/
 * DisciplinaryAction/Resignation/Termination/Remarks block beneath it, for either the Minor/Major
 * Case flow or the AWOL flow (`isAwol` selects which). Fully self-sufficient: fetches its own
 * fileFieldTypes/decisions/disciplinaryActions and reads its own case-relevant form fields via
 * useWatch, rather than receiving them as props from the page - see the "sections own their data"
 * principle in the IncidentForm.tsx refactor plan. Reports pathAComplete/monitorCanComplete/
 * pendingCaseDocumentSubmit upward via IncidentMonitoringGatingContext, since
 * IncidentFormActions's Submit/Complete buttons live in a different part of the page. Must be
 * rendered under an IncidentRecordFileProvider (via useCaseDocumentChain).
 */
const MonitoringCaseSection = withMemoizedContainer(
  FormSection,
  (
    {
      control,
      incidentRecordId,
      setValue,
      isAwol,
      loading,
      ...sectionProps
    }: MonitoringCaseSectionProps,
    Container,
  ) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [, setGating] = useIncidentMonitoringGating();
    const [{ currentUser }, { isInAnyRole }] = useAuthContext();
    const isUserLRD = isInAnyRole("Labor Relations Department");
    // Per BA confirmation: acknowledgement + signed-copy upload are Coordinator-only, not just
    // "any non-LRD role" - the system has at least 4 roles, so this must check the Coordinator
    // role specifically rather than negate isUserLRD.
    const isUserCoordinator = isInAnyRole("Coordinator");

    const incidentTypeName = useWatch({ control, name: "incidentTypeName" });
    const recordCreatedBy = useWatch({ control, name: "createdBy" });
    const status = useWatch({ control, name: "statusName" });
    const isCompleted = status === "Completed";
    const resignationValue = useWatch({ control, name: "resignation" });
    const terminationValue = useWatch({ control, name: "termination" });
    const decisionId = useWatch({ control, name: "decisionId" });
    const disciplinaryActionId = useWatch({
      control,
      name: "disciplinaryActionId",
    });
    const disciplinaryLocked = !!resignationValue;

    const { data: fileFieldTypes = [] } = useQuery({
      queryKey: ["incident-file-field-types"],
      queryFn: () => getAllIncidentFileFieldTypes(),
    });
    const fileFieldType = buildFileFieldTypeMap(fileFieldTypes);

    const { data: decisionOptions = [] } = useQuery({
      queryKey: ["incident-decisions"],
      queryFn: async () => toEnumOptions(await getAllDecisions()),
      enabled: !isAwol,
    });
    const { data: disciplinaryActionOptions = [] } = useQuery({
      queryKey: ["incident-disciplinary-actions"],
      queryFn: async () => toEnumOptions(await getAllDisciplinaryActions()),
      enabled: !isAwol,
    });
    const decision = decisionOptions.find((d) => d.id === decisionId) ?? null;
    const disciplinaryAction =
      disciplinaryActionOptions.find((d) => d.id === disciplinaryActionId) ??
      null;

    const { slots: caseDocumentSlots } = useCaseDocumentChain({
      incidentTypeName: incidentTypeName ?? "",
      fileFieldType,
      isUserLRD,
      isUserCoordinator,
      currentUserId: currentUser?.id,
      disciplinaryLocked,
      recordCreatedBy,
    });

    // On load, default to the tab for whichever document type was uploaded (unsigned) most
    // recently, rather than always starting on the first tab - lets the user land straight back
    // on whatever they were last working on. Runs once, the first time slots/files are actually
    // available; afterward the user's own tab clicks take over.
    const hasSetInitialTab = useRef(false);
    useEffect(() => {
      if (hasSetInitialTab.current || caseDocumentSlots.length === 0) return;
      hasSetInitialTab.current = true;

      const slotsWithUnsignedUploads = caseDocumentSlots.filter(
        (slot) => slot.unsignedFile,
      );
      if (slotsWithUnsignedUploads.length === 0) return;

      const mostRecentSlot = slotsWithUnsignedUploads.reduce((latest, slot) =>
        (slot.unsignedFile!.createDate?.getTime() ?? 0) >
        (latest.unsignedFile!.createDate?.getTime() ?? 0)
          ? slot
          : latest,
      );
      const mostRecentIndex = caseDocumentSlots.indexOf(mostRecentSlot);
      if (mostRecentIndex >= 0) setSelectedTab(mostRecentIndex);
    }, [caseDocumentSlots]);

    const nodaSlot = caseDocumentSlots.find(
      (s) => s.key === "noticeOfDecisionAndAction",
    );
    const tbwlSlot = caseDocumentSlots.find(
      (s) => s.key === "transferBackToWorkLetter",
    );
    const awolLetterSlot = caseDocumentSlots.find(
      (s) => s.key === "awolLetter",
    );

    // Bug 157953 (3rd recurrence): AWOL's completion chain is AWOL Letter -> NOH -> T/BWL, and per
    // AwolCaseCompletionRule.cs (Gap 2.7) that's the ENTIRE completion rule for AWOL - the backend
    // has no Decision/DisciplinaryAction stage for AWOL at all, only HasSignedAwolLetter &&
    // HasSignedTransferBackToWorkLetter. The two prior fixes here only patched pathAComplete's
    // signed-file check; they left the AWOL "Decision" ComboBox (a local-only, never-persisted UI
    // enum) rendering and gating monitorCanComplete once pathAComplete was reached, which is what
    // testers kept seeing as "the Decision section becomes available after uploading the Signed
    // T/BWL". Per Task 157795, that field/section needed to be removed entirely, not re-gated -
    // the AWOL_DECISION_OPTIONS ComboBox, awolDecisionId field, and nh2Unacknowledged (dead code,
    // never set to true anywhere) are now gone; pathAComplete alone drives AWOL completion.
    const pathAComplete = isAwol
      ? Boolean(awolLetterSlot?.isSignedConfirmed) &&
        Boolean(tbwlSlot?.isSignedConfirmed)
      : Boolean(nodaSlot?.isSignedConfirmed);
    const pathBComplete = !!resignationValue;
    const pathCComplete = !!terminationValue;

    // AWOL has no Decision field to lock Resignation on anymore - lock it once Termination has
    // been used instead, keeping the two early-exit paths mutually exclusive (mirrors
    // disciplinaryLocked, which locks Termination once Resignation has been used).
    const pathBLocked = isAwol ? Boolean(terminationValue) : decision != null;
    // Bug 157822: Resignation/Termination upload must only be enabled for the record's creator
    // (LRD reviews/processes the case; they aren't the party resigning/being terminated), and is
    // now enforced server-side too (UploadIncidentRecordFileValidator, PR 2827). IncidentRecordDto
    // exposes createdBy as of that same PR, so this does a precise creator check instead of the
    // earlier "not LRD" proxy - matches the createdBy/currentUserId comparison useCaseDocumentChain
    // already uses for per-file ownership. Compared case-insensitively, same as
    // UploadIncidentRecordFileValidator.cs's own CreatedBy check server-side - otherwise a
    // same-user but differently-cased id (e.g. B2C object id case differences) makes the UI hide
    // the upload for the actual creator even though the server would accept it.
    const canEditResignationTermination =
      Boolean(recordCreatedBy) &&
      recordCreatedBy?.toLowerCase() === currentUser?.id?.toLowerCase();

    // Bug 157954: Termination should be available for AWOL "throughout the entire process" - with
    // the Decision field removed (157953), there's no longer anything AWOL-specific to lock it on;
    // pathCLocked below still locks it once Resignation has been used (disciplinaryLocked).
    const terminationUnlocked = isAwol
      ? true
      : Boolean(
          pathAComplete &&
          decision?.code === "GUILTY" &&
          disciplinaryAction?.code === "TERMINATION",
        );
    const pathCLocked = disciplinaryLocked || !terminationUnlocked;

    const outcomeResolvedWithoutTermination =
      !isAwol &&
      (decision?.code === "ABSOLVED" ||
        (decision?.code === "GUILTY" &&
          disciplinaryAction != null &&
          disciplinaryAction.code !== "TERMINATION"));

    const monitorCanComplete = isAwol
      ? pathAComplete || pathBComplete || pathCComplete
      : pathBComplete || pathCComplete || outcomeResolvedWithoutTermination;

    const activeSlot = caseDocumentSlots[selectedTab];
    // getPendingSubmit builds a fresh object literal on every call - memoized against the
    // active slot's own primitive fields (not the ever-recreated `caseDocumentSlots` array
    // reference) so this stays referentially stable across renders where nothing actually
    // changed. Without this, the effect below re-fires every render whenever a pending
    // submit exists (e.g. right after an unconfirmed upload), triggering setGating -> re-render
    // -> new object -> effect fires again, forever ("Maximum update depth exceeded").
    const pendingCaseDocumentSubmit = useMemo(
      () => getPendingSubmit(activeSlot),
      [
        activeSlot?.key,
        activeSlot?.unsignedFile?.id,
        activeSlot?.isUnsignedConfirmed,
        activeSlot?.unsignedPermissionMode,
        activeSlot?.singleUploadOnly,
        activeSlot?.signedFile?.id,
        activeSlot?.isSignedConfirmed,
        activeSlot?.signedPermissionMode,
      ],
    );

    useEffect(() => {
      setGating({
        pathAComplete,
        monitorCanComplete,
        pendingCaseDocumentSubmit,
      });
    }, [
      pathAComplete,
      monitorCanComplete,
      pendingCaseDocumentSubmit,
      setGating,
    ]);

    return (
      <Container loading={loading} {...sectionProps}>
        <div style={{ marginBottom: 16 }}>
          <CaseDocumentTabStrip
            slots={caseDocumentSlots}
            selected={selectedTab}
            onSelect={setSelectedTab}
            incidentRecordId={incidentRecordId}
            loading={loading}
          />
        </div>

        <div
          style={{
            border: "1px solid var(--kendo-color-border, #d6d6d6)",
            borderRadius: 4,
            padding: 16,
          }}
        >
          {/* Bug 157953: AWOL has no Decision/DisciplinaryAction stage at all (see
              AwolCaseCompletionRule.cs Gap 2.7) - this section only ever applies to Minor/Major
              Case, where pathAComplete (signed Notice of Decision and Action) gates it. */}
          {!isAwol && pathAComplete && (
            <FormFieldSet cols={3} style={{ marginBottom: 16 }}>
              <ControlledComboBox
                name="decisionId"
                control={control}
                label="Decision"
                data={decisionOptions}
                dataItemKey="id"
                textField="name"
                // Decision is the acting Coordinator's call, not LRD's - per Bug 157823,
                // LRD must not be able to select a Decision even when the record is
                // otherwise editable (mirrors the isUserCoordinator/isUserLRD role gating
                // used elsewhere in this section, e.g. useCaseDocumentChain's
                // canEditAcknowledge).
                readOnly={disciplinaryLocked || isUserLRD || isCompleted}
                required={!disciplinaryLocked}
              />
              {decision?.code === "GUILTY" && (
                <ControlledComboBox
                  name="disciplinaryActionId"
                  control={control}
                  label="Disciplinary Action"
                  data={disciplinaryActionOptions}
                  dataItemKey="id"
                  textField="name"
                  readOnly={disciplinaryLocked || isUserLRD || isCompleted}
                  required={!disciplinaryLocked}
                />
              )}
              <div />
            </FormFieldSet>
          )}

          <ResignationTerminationRow
            control={control}
            setValue={setValue}
            incidentRecordId={incidentRecordId}
            resignationFileFieldTypeId={fileFieldType.RESIGNATION}
            terminationFileFieldTypeId={fileFieldType.TERMINATION}
            resignationLocked={
              pathBLocked || !canEditResignationTermination || isCompleted
            }
            terminationLocked={
              pathCLocked || !canEditResignationTermination || isCompleted
            }
            // Only mandatory for the Minor/Major "Guilty + Termination" outcome (Bug 157954:
            // AWOL's Termination is meant to be available like Resignation, not forced required
            // just because an AWOL Decision was picked).
            terminationRequired={!isAwol && terminationUnlocked}
            loading={loading}
          />

          <ControlledTextArea
            name="remarks"
            control={control}
            label="Remarks"
            readOnly={!monitorCanComplete || isCompleted}
            style={{ marginTop: 16 }}
          />
        </div>
      </Container>
    );
  },
);

export default MonitoringCaseSection;
